import { StyleContentWrapper } from '@pages/front/commons/ContentWrapper/style'
import { TextWrapper } from '@pages/front/commons/Text'
import {
  IconWrapper,
  StyleFormFieldRow,
  StyleFormFieldWrapper,
  StyleFormLeftContent,
  StyleFormRightWrapper,
  StyleFormWrapper,
  StyleInputField,
  StyleLabel,
  StyleTextArea,
} from './style'
import { StyleText } from '@pages/front/commons/Text/style'
import { StyleSvgWrappeer } from '@pages/front/commons/SvgIcon/style'
import {
  coloredCallIcon,
  coloredEmailIcon,
  coloredFacebook,
  coloredInsta,
  coloredLocationIcon,
  coloredTwitter,
} from '@pages/front/svgs/svg'
import Button from '@components/button'
import { motion } from 'framer-motion'

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: 'beforeChildren',
    },
  },
}

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 10,
    },
  },
}

const formItemVariants = {
  hidden: { x: 20, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 10,
    },
  },
}

const iconVariants = {
  hover: {
    scale: 1.1,
    transition: { type: 'spring', stiffness: 400, damping: 10 },
  },
  tap: { scale: 0.9 },
}

const buttonVariants = {
  hover: {
    scale: 1.03,
    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
    transition: { type: 'spring', stiffness: 400, damping: 10 },
  },
  tap: { scale: 0.98 },
}

// Create animated components
const AnimatedFormWrapper = motion(StyleFormWrapper)
const AnimatedLeftContent = motion(StyleFormLeftContent)
const AnimatedRightWrapper = motion(StyleFormRightWrapper)
const AnimatedFormFieldRow = motion(StyleFormFieldRow)
const AnimatedInputField = motion(StyleInputField)
const AnimatedTextArea = motion(StyleTextArea)
const AnimatedIconWrapper = motion(IconWrapper)

const Form = () => {
  return (
    <StyleContentWrapper stretch>
      <TextWrapper
        title="Expert Help, One Message Away"
        subtitle={`"Your concerns matter. Let us help, anytime."`}
      />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <AnimatedFormWrapper>
          <AnimatedLeftContent variants={containerVariants}>
            <motion.div variants={itemVariants}>
              <StyleText color="#262f77" fontSize="24" weight="600">
                Get In Touch
              </StyleText>
            </motion.div>

            <motion.div variants={itemVariants}>
              <StyleText color="#555" fontSize="16" lineHeight="1.5">
                We really appreciate you taking the time to get in touch. Please
                fill in the form below.
              </StyleText>
            </motion.div>

            <motion.div variants={itemVariants}>
              <StyleText color="#262f77" fontSize="18" weight="600">
                Contact
              </StyleText>
            </motion.div>

            <motion.div variants={itemVariants}>
              <StyleText color="#414146" fontSize="14">
                <StyleSvgWrappeer
                  dangerouslySetInnerHTML={{ __html: coloredCallIcon }}
                />
                +977 9876543210
              </StyleText>
            </motion.div>

            <motion.div variants={itemVariants}>
              <StyleText color="#414146" fontSize="14">
                <StyleSvgWrappeer
                  dangerouslySetInnerHTML={{ __html: coloredEmailIcon }}
                />
                info@Supchar.com.np
              </StyleText>
            </motion.div>

            <motion.div variants={itemVariants}>
              <StyleText color="#414146" fontSize="14">
                <StyleSvgWrappeer
                  dangerouslySetInnerHTML={{ __html: coloredLocationIcon }}
                />
                Kathmandu, Nepal
              </StyleText>
            </motion.div>

            <AnimatedIconWrapper variants={itemVariants}>
              <motion.div
                variants={iconVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <StyleSvgWrappeer
                  dangerouslySetInnerHTML={{ __html: coloredFacebook }}
                />
              </motion.div>
              <motion.div
                variants={iconVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <StyleSvgWrappeer
                  dangerouslySetInnerHTML={{ __html: coloredInsta }}
                />
              </motion.div>
              <motion.div
                variants={iconVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <StyleSvgWrappeer
                  dangerouslySetInnerHTML={{ __html: coloredTwitter }}
                />
              </motion.div>
            </AnimatedIconWrapper>
          </AnimatedLeftContent>

          <AnimatedRightWrapper variants={containerVariants}>
            <motion.div variants={itemVariants}>
              <StyleText color="#262f77" fontSize="20" weight="600">
                We're Here to Help!
              </StyleText>
            </motion.div>

            <StyleFormFieldWrapper>
              <AnimatedFormFieldRow variants={formItemVariants}>
                <StyleLabel htmlFor="name">Name</StyleLabel>
                <AnimatedInputField
                  type="text"
                  placeholder="Enter your full name"
                  id="name"
                  whileFocus={{
                    scale: 1.01,
                    boxShadow: '0px 0px 5px rgba(38, 47, 119, 0.2)',
                  }}
                />
              </AnimatedFormFieldRow>

              <AnimatedFormFieldRow variants={formItemVariants}>
                <StyleLabel htmlFor="phone">Contact Number</StyleLabel>
                <AnimatedInputField
                  type="tel"
                  placeholder="Enter your phone number"
                  id="phone"
                  whileFocus={{
                    scale: 1.01,
                    boxShadow: '0px 0px 5px rgba(38, 47, 119, 0.2)',
                  }}
                />
              </AnimatedFormFieldRow>

              <AnimatedFormFieldRow variants={formItemVariants}>
                <StyleLabel htmlFor="email">Email</StyleLabel>
                <AnimatedInputField
                  type="email"
                  placeholder="Enter your email"
                  id="email"
                  whileFocus={{
                    scale: 1.01,
                    boxShadow: '0px 0px 5px rgba(38, 47, 119, 0.2)',
                  }}
                />
              </AnimatedFormFieldRow>

              <AnimatedFormFieldRow variants={formItemVariants}>
                <StyleLabel htmlFor="subject">Subject</StyleLabel>
                <AnimatedInputField
                  type="text"
                  placeholder="Enter subject"
                  id="subject"
                  whileFocus={{
                    scale: 1.01,
                    boxShadow: '0px 0px 5px rgba(38, 47, 119, 0.2)',
                  }}
                />
              </AnimatedFormFieldRow>

              <AnimatedFormFieldRow variants={formItemVariants}>
                <StyleLabel htmlFor="message">Your Message</StyleLabel>
                <AnimatedTextArea
                  id="message"
                  placeholder="Write your message here..."
                  rows={5}
                  whileFocus={{
                    scale: 1.01,
                    boxShadow: '0px 0px 5px rgba(38, 47, 119, 0.2)',
                  }}
                />
              </AnimatedFormFieldRow>

              <AnimatedFormFieldRow variants={formItemVariants}>
                <Button title="Send Message" />
              </AnimatedFormFieldRow>
            </StyleFormFieldWrapper>
          </AnimatedRightWrapper>
        </AnimatedFormWrapper>
      </motion.div>
    </StyleContentWrapper>
  )
}

export default Form
